
import { useState, useEffect } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";
import { CompanyData, Connection } from '@/types';
import { Json } from '@/integrations/supabase/types';
import { useLocation } from 'react-router-dom';
import { parseProducts, parseConnections } from '@/utils/jsonHelpers';
import { normalizeConnectionForStorage } from '@/utils/organization/saveOrganization';
import { draftTokenField, rememberDraftToken } from '@/utils/onboarding/draftToken';
import { generateStableIdentifier } from '@/utils/magic-link/helpers';
import {
  ensureOnboardingSessionFor,
  getOnboardingSessionOwner,
  markOnboardingSessionOwner,
  readOnboardingOrganisationId,
  beginConnectionSave,
  currentConnectionSaveEpoch,
  finishConnectionSave,
  readOwnedConnectionIds,
  retainUnsavedConnectionIds,
  writeOwnedConnectionIds,
} from '@/utils/onboarding/sessionState';
import { pickOrganisationForMagicLink } from '@/utils/magic-link/resolveMagicLinkOrganisation';


/**
 * True when in-session connection ids are unsaved work for `orgId`.
 *
 * Company data covers an anonymous form that already names this organisation.
 * A different real owner is cleared before ids are read, so that match cannot
 * preserve another organisation's connectors.
 */
const sessionAlreadyBelongsToOrg = (orgId: string | null | undefined): boolean => {
  if (!orgId) return false;
  if (getOnboardingSessionOwner() === orgId) return true;
  return readOnboardingOrganisationId() === orgId;
};

/**
 * Record an organisation's persisted connection ids as the session's own.
 * Ownership is stamped alongside the ids so a later form cannot read them back
 * under a different organisation.
 *
 * Null means the column has never been written. Reloading the same organisation
 * must not drop ids this tab added and has not saved yet. That does not apply
 * after an anonymous session is adopted onto a different org: those ids still
 * belong to the in-progress form, and keeping them lets a later save write
 * them onto the organisation just loaded. The belonging check runs before
 * `ensureOnboardingSessionFor`, which re-stamps an anonymous session without
 * clearing its ids.
 *
 * An empty list is a persisted removal: replace this tab's ids so the next
 * draft save cannot append connectors that no longer belong to the organisation.
 * Connectors this tab added, and has not successfully saved, stay. A reload
 * that still shows `[]` while that save is in flight — or after it fails —
 * would otherwise drop the session id, and the next save would omit them.
 * The in-progress connection row stays with that id. Keeping only the id makes
 * the next save store a generic placeholder and drop the connector's name,
 * service, and import details.
 *
 * `fetchEpoch` is the save epoch from when this snapshot was requested. A
 * response that started before the save finished is stale and must not drop
 * the connector that save is writing.
 *
 * Switching organisations discards the previous session before the loaded
 * organisation's ids are recorded, so a clear cannot wipe ids just written.
 *
 * Returns the connection rows to store on the loaded organisation: the persisted
 * list, plus any unsaved row this tab still owns.
 */
const isConnectionRecord = (value: unknown): value is Connection => {
  if (!value || typeof value !== 'object') return false;
  const id = (value as { id?: unknown }).id;
  return typeof id === 'string' && id.length > 0;
};

const connectionRecordsFromUnknown = (value: unknown): Connection[] => {
  if (Array.isArray(value)) {
    return value.filter(isConnectionRecord).map((conn) => ({ ...conn }));
  }

  if (!value || typeof value !== 'object') return [];

  return Object.entries(value as Record<string, unknown>).flatMap(([key, entry]) => {
    if (!entry || typeof entry !== 'object') return [];
    const conn = entry as Connection;
    const id = typeof conn.id === 'string' && conn.id.length > 0 ? conn.id : key;
    if (!id) return [];
    return [{ ...conn, id }];
  });
};

/** In-progress connectors for this organisation, including fields a placeholder would drop. */
const readInProgressConnectionRecords = (orgId: string | null | undefined): Connection[] => {
  if (!sessionAlreadyBelongsToOrg(orgId) || typeof sessionStorage === 'undefined') return [];

  const raw = sessionStorage.getItem('figabl_company_data');
  if (!raw) return [];

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return [];

    const company = parsed as {
      organization_id?: unknown;
      organisation_id?: unknown;
      connections?: unknown;
    };
    const storedOrgId = [company.organization_id, company.organisation_id].find(
      (id): id is string => typeof id === 'string' && id.length > 0,
    );
    if (storedOrgId && orgId && storedOrgId !== orgId) return [];

    return connectionRecordsFromUnknown(company.connections);
  } catch {
    return [];
  }
};

const withUnsavedConnectionRecords = (
  persisted: Connection[],
  retainedIds: readonly string[],
  orgId: string | null | undefined,
): Connection[] => {
  if (retainedIds.length === 0) return persisted;

  const present = new Set(persisted.map((conn) => conn.id).filter((id) => id.length > 0));
  const retained = new Set(retainedIds);
  const unsavedById = new Map<string, Connection>();
  for (const conn of readInProgressConnectionRecords(orgId)) {
    if (!retained.has(conn.id) || present.has(conn.id)) continue;
    unsavedById.set(conn.id, conn);
  }

  if (unsavedById.size === 0) return persisted;

  const merged = [...persisted];
  for (const id of retainedIds) {
    const record = unsavedById.get(id);
    if (record) merged.push(record);
  }
  return merged;
};

const seedConnectionIdsForOrg = (
  orgId: string | null | undefined,
  connections: unknown,
  fetchEpoch: number = currentConnectionSaveEpoch(),
): Connection[] => {
  const preserveUnsavedIds = sessionAlreadyBelongsToOrg(orgId);
  ensureOnboardingSessionFor(orgId ?? null);

  if (connections == null && preserveUnsavedIds) {
    const owned = readOwnedConnectionIds(orgId ?? null);
    const ownedIds = [...owned.standard, ...owned.csv];
    if (ownedIds.length > 0) return withUnsavedConnectionRecords([], ownedIds, orgId);
  }

  const persisted = parseConnections(connections as Json);
  const persistedIds = persisted
    .map((conn) => conn.id)
    .filter((id): id is string => Boolean(id));

  const owned = preserveUnsavedIds
    ? readOwnedConnectionIds(orgId ?? null)
    : { standard: [] as string[], csv: [] as string[] };
  const retained = preserveUnsavedIds
    ? retainUnsavedConnectionIds(persistedIds, [...owned.standard, ...owned.csv], fetchEpoch)
    : [];
  const connectionIds = [...persistedIds];
  for (const id of retained) {
    if (!connectionIds.includes(id)) connectionIds.push(id);
  }

  writeOwnedConnectionIds(orgId ?? null, {
    standard: connectionIds.filter((id) => !id.startsWith('csv-import-')),
    csv: connectionIds.filter((id) => id.startsWith('csv-import-')),
  });

  return withUnsavedConnectionRecords(persisted, retained, orgId);
};

export const useFormDrafts = () => {
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [formCompleted, setFormCompleted] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [pendingSave, setPendingSave] = useState<CompanyData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tokenDetected, setTokenDetected] = useState(false);
  const [isVerifyingToken, setIsVerifyingToken] = useState(false);
  const [disableEin, setDisableEin] = useState(false);
  const { toast } = useToast();
  const location = useLocation();

  const getTokenFromURL = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('token');
  };

  const getSubmissionIdentifierFromURL = () => {
    const urlParams = new URLSearchParams(location.search);
    const urlSubmissionId = urlParams.get('submission_identifier');
    // If not in URL, try sessionStorage, otherwise default to 'default'
    return urlSubmissionId || sessionStorage.getItem('figabl_submission_identifier') || 'default';
  };

  const getSpecificOrganizationFromURL = () => {
    const urlParams = new URLSearchParams(location.search);
    const orgId = urlParams.get('org_id') || urlParams.get('organisation_id');
    const draftId = urlParams.get('draft_id');
    const submissionIdentifier = getSubmissionIdentifierFromURL();

    return {
      organisationId: orgId,
      draftId: draftId,
      submissionIdentifier: submissionIdentifier,
      hasSpecificOrg: !!(orgId || draftId)
    };
  };

  const verifyMagicLink = async (token: string) => {
    setIsVerifyingToken(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      const { data: authData, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        throw sessionError;
      }

      // Check if this is a specific organization magic link
      const {
        organisationId,
        draftId: specificDraftId,
        hasSpecificOrg,
        submissionIdentifier
      } = getSpecificOrganizationFromURL();

      if (!authData.session) {
        const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: 'magiclink'
        });

        if (verifyError) {
          throw verifyError;
        }

        if (verifyData.session) {
          setSession(verifyData.session);

          // Check for the authenticated user's email in the magic_link_sessions table
          // (either as primary email or alternative email)
          const email = verifyData.session.user.email;

          // First, look up the session by submission_identifier to get the canonical data
          const { data: sessionData, error: sessionError } = await supabase
            .from('magic_link_sessions')
            .select('*')
            .eq('submission_identifier', submissionIdentifier)
            .maybeSingle();

          if (sessionError && sessionError.code !== 'PGRST116') {
            console.error('Error retrieving magic link session:', sessionError);
          }

          // Check if the authenticated email matches either primary or alternative emails
          let primarySessionData = null;
          let altSessionData = null;

          if (sessionData) {
            // Check if email matches primary email
            if (sessionData.email === email) {
              primarySessionData = sessionData;
            } else {
              // Check if email is in alternative emails
              const altEmails = sessionData.alternative_email;
              const isAlternativeEmail = altEmails && (
                (Array.isArray(altEmails) && altEmails.includes(email)) ||
                (typeof altEmails === 'string' && altEmails === email)
              );

              if (isAlternativeEmail) {
                altSessionData = [sessionData]; // Format as array to match RPC response
              }
            }
          }
          // Find the matching session with the right submission identifier
          const hasAlternativeEmailMatch = Array.isArray(altSessionData) && altSessionData.length > 0;
          const alternativeEmailData = hasAlternativeEmailMatch
            ? altSessionData.find(session => session.submission_identifier === submissionIdentifier)
            : null;

          // Determine if this is an alternative email login
          const isAlternativeEmail = !!alternativeEmailData;

          // Load specified organization if present in URL
          if (hasSpecificOrg) {
            if (specificDraftId) {
              await loadDraftById(specificDraftId);
            } else if (organisationId) {
              await loadOrganisationById(organisationId, submissionIdentifier);
            } else if (isAlternativeEmail && alternativeEmailData?.organisation_id) {
              // If user logged in with alternative email, load the organization associated with that email
              await loadOrganisationById(alternativeEmailData.organisation_id, submissionIdentifier);
            } else {
              await loadSavedProgress(
                verifyData.session.user.id,
                verifyData.session.user.email,
                submissionIdentifier
              );
            }
          } else {
            // If using alternative email, check if there's an organization linked to that session
            if (isAlternativeEmail && alternativeEmailData?.organisation_id) {
              await loadOrganisationById(alternativeEmailData.organisation_id, submissionIdentifier);
            } else {
              await loadSavedProgress(
                verifyData.session.user.id,
                verifyData.session.user.email,
                submissionIdentifier
              );
            }
          }

          if (pendingSave) {
            await saveDraft(pendingSave, verifyData.session, submissionIdentifier);
            setPendingSave(null);
          }

          toast({
            title: "Authenticated successfully",
            description: "Your progress has been loaded."
          });

          setIsVerifyingToken(false);
          setTokenDetected(false);
          setIsLoading(false);

          if (window.history.replaceState) {
            // Keep org_id or draft_id in the URL if they exist, but remove the token
            const currentParams = new URLSearchParams(window.location.search);
            currentParams.delete('token');
            const newParams = currentParams.toString();
            const newUrl = window.location.pathname + (newParams ? `?${newParams}` : '');
            window.history.replaceState({}, document.title, newUrl);
          }

          return;
        }

        // Check for various types of tokens in the URL fragment
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const recoveryToken = hashParams.get('recovery_token');
        if (recoveryToken) {
          // Handle password recovery
          // Example: navigate to a password reset page
          return;
        }

        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        if (accessToken && refreshToken) {
          // Handle the tokens as needed
          return;
        }
      } else {
        setSession(authData.session);

        // Check if the authenticated email is an alternative email
        const email = authData.session.user.email;

        // Check if the email is an alternative email
        const { data: altSessionData, error: altSessionError } = await (supabase
          .rpc as any)('find_session_by_alternative_email', { search_email: email });

        if (altSessionError) {
          console.error('Error checking alternative email:', altSessionError);
        }

        const hasAlternativeEmailMatch = Array.isArray(altSessionData) && altSessionData.length > 0;
        const alternativeEmailData = hasAlternativeEmailMatch
          ? altSessionData.find(session => session.submission_identifier === submissionIdentifier)
          : null;

        // Load specified organization if present in URL
        if (hasSpecificOrg) {
          if (specificDraftId) {
            await loadDraftById(specificDraftId);
          } else if (organisationId) {
            await loadOrganisationById(organisationId, submissionIdentifier);
          } else if (alternativeEmailData?.organisation_id) {
            // If user logged in with alternative email, load the organization associated with that email
            await loadOrganisationById(alternativeEmailData.organisation_id, submissionIdentifier);
          } else {
            await loadSavedProgress(
              authData.session.user.id,
              authData.session.user.email,
              submissionIdentifier
            );
          }
        } else {
          // If using alternative email, check if there's an organization linked to that session
          if (alternativeEmailData?.organisation_id) {
            await loadOrganisationById(alternativeEmailData.organisation_id, submissionIdentifier);
          } else {
            await loadSavedProgress(
              authData.session.user.id,
              authData.session.user.email,
              submissionIdentifier
            );
          }
        }

        if (pendingSave) {
          await saveDraft(pendingSave, authData.session, submissionIdentifier);
          setPendingSave(null);
        }
      }

      toast({
        title: "Authenticated successfully",
        description: "Your progress has been loaded."
      });
    } catch (error: any) {
      toast({
        title: "Authentication error",
        description: error.message || "Failed to process magic link",
        variant: "destructive",
      });
    } finally {
      setIsVerifyingToken(false);
      setTokenDetected(false);
      setIsLoading(false);

      if (window.history.replaceState) {
        // Keep org_id or draft_id in the URL if they exist, but remove the token
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.delete('token');
        const newParams = currentParams.toString();
        const newUrl = window.location.pathname + (newParams ? `?${newParams}` : '');
        window.history.replaceState({}, document.title, newUrl);
      }
    }
  };

  // Add new function to load an organization by ID
  const loadOrganisationById = async (organisationId: string, submissionIdentifier: string = 'default') => {
    const connectionSnapshotEpoch = currentConnectionSaveEpoch();
    try {
      setIsLoading(true);

      // Check if this org is linked to a specific submission
      const { data: sessionData } = await supabase
        .from('magic_link_sessions')
        .select('*')
        .eq('organisation_id', organisationId)
        .eq('submission_identifier', submissionIdentifier)
        .maybeSingle();

      const { data: org, error } = await supabase
        .from('organisations')
        .select('*')
        .eq('id', organisationId)
        .single();

      if (error) {
        throw error;
      }

      if (org) {
        // Format organization data to match CompanyData structure
        const formatOrg: CompanyData = {
          user_id: org.user_id,
          pocName: org.poc_name,
          pocEmail: org.poc_email,
          pocPhone: org.poc_phone || '',
          pocPhoneVerified: org.poc_phone_verified || false,
          name: org.name,
          name_dbas: org.name_dbas || '',
          address: org.address,
          stateOfIncorporation: org.state_of_incorporation,
          ein: org.ein,
          revenueLast12Months: org.revenue_last_12_months || undefined,
          products: parseProducts(org.products),
          connections: seedConnectionIdsForOrg(org.id, org.connections, connectionSnapshotEpoch),
          // Store the organization_id so we know we're editing an existing entry
          organization_id: organisationId
        };

        setDisableEin(true);
        formatOrg.terms = true;
        setCompanyData(formatOrg);

        toast({
          title: "Organization loaded",
          description: "You can now edit this organization's information."
        });
      }
    } catch (error) {
      console.error('Error loading organization by ID:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveDraft = async (
    data: CompanyData,
    currentSession: Session | null = session,
    submissionIdentifier: string = 'default'
  ) => {
    const emailToUse = data.pocEmail || '';
    if (!emailToUse) {
      toast({
        title: "Error",
        description: "An email is required to save your progress",
        variant: "destructive"
      });
      return false;
    }

    const dataToSave: CompanyData = {
      ...data,
      products: Array.isArray(data.products) ? data.products : parseProducts(data.products as unknown as Json)
    };

    // Try to get organization_id from multiple sources
    let organizationId = data.organization_id || companyData?.organization_id;

    // Fallback: get from sessionStorage
    if (!organizationId) {
      const savedData = sessionStorage.getItem('figabl_company_data');
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          organizationId = parsedData.organization_id;
        } catch (error) {
          console.error('Error parsing sessionStorage for organization_id:', error);
        }
      }
    }

    // Fallback: check if there's an existing draft organization with same EIN + email
    if (!organizationId && dataToSave.ein && emailToUse) {
      try {
        const { data: existingOrgs } = await supabase
          .from('organisations')
          .select('id, is_test_clone')
          .eq('ein', dataToSave.ein)
          .eq('poc_email', emailToUse)
          // FIG-345 UC-225: still onboarding. filter() rather than neq(): the generated
          // types predate onboarding_state, and neq() on it hits TS2589.
          .filter('onboarding_state', 'neq', 'submitted')
          .order('is_test_clone', { ascending: true });

        const existingOrg = pickOrganisationForMagicLink(existingOrgs ?? [], { allowTestClone: true });
        if (existingOrg?.id) {
          organizationId = existingOrg.id;
        }
      } catch (error) {
        console.error('Error checking for existing draft organization:', error);
      }
    }

    // Set the organization_id if we found one
    if (organizationId) {
      dataToSave.organization_id = organizationId;
      // Persist to sessionStorage for future navigation
      const currentSessionData = sessionStorage.getItem('figabl_company_data');
      if (currentSessionData) {
        try {
          const parsedSessionData = JSON.parse(currentSessionData);
          parsedSessionData.organization_id = organizationId;
          sessionStorage.setItem('figabl_company_data', JSON.stringify(parsedSessionData));
        } catch (error) {
          console.error('Error updating sessionStorage with organization_id:', error);
        }
      }
    }

    // Ensure connections is always an array
    if (!dataToSave.connections) {
      dataToSave.connections = [];
    }

    const storedConnections = readOwnedConnectionIds(organizationId ?? null);
    const connectionIds = [...storedConnections.standard, ...storedConnections.csv];
    const preservedById = new Map(
      readInProgressConnectionRecords(organizationId ?? null).map((connection) => [connection.id, connection]),
    );

    if (connectionIds.length > 0) {
      connectionIds.forEach(connectionId => {
        if (connectionId && !dataToSave.connections?.some(conn => conn.id === connectionId)) {
          const preserved = preservedById.get(connectionId);
          if (preserved) {
            dataToSave.connections.push(preserved);
            return;
          }

          const connectionType = connectionId.startsWith('csv-import-') ? 'csv' : 'other';
          const connection: Connection = {
            id: connectionId,
            type: connectionType,
            name: connectionType === 'csv' ? 'CSV Import' : 'Data Connection',
            status: 'active',
            created_at: new Date().toISOString()
          };

          dataToSave.connections.push(connection);
        }
      });
    }

    const connectionSave = beginConnectionSave(
      (dataToSave.connections ?? []).map((connection) => connection.id).filter((id) => id.length > 0),
    );
    let draftSaved = false;

    // Update magic_link_sessions if we have an email but no session
    if (!currentSession?.user?.id) {
      try {
        // Use edge function to save draft (anonymous)
        const organizationData = {
          poc_name: dataToSave.pocName,
          poc_email: dataToSave.pocEmail,
          poc_phone: dataToSave.pocPhone || null,
          poc_phone_verified: dataToSave.pocPhoneVerified || false,
          name: dataToSave.name,
          name_dbas: dataToSave.name_dbas || null,
          address: dataToSave.address || '',
          state_of_incorporation: dataToSave.stateOfIncorporation || '',
          ein: dataToSave.ein || '',
          products: dataToSave.products as unknown as Json,
          connections: (Array.isArray(dataToSave.connections) ? dataToSave.connections.map(normalizeConnectionForStorage) : []) as unknown as Json,
          revenue_last_12_months: dataToSave.revenueLast12Months || null,
          onboarding_state: 'in_progress' as const,
        };

        const { data: fnRes, error: fnErr } = await invokeFunction(supabase, 'organisations-manager-v3', {
          body: {
            action: 'save_draft',
            data: organizationData,
            organization_id: dataToSave.organization_id || null,
            ...draftTokenField(dataToSave.organization_id),
          }
        });

        if (fnErr || !fnRes?.success) {
          console.error('Edge function error (save_draft anon):', fnErr || fnRes);
          throw fnErr || new Error(fnRes?.error || 'Failed to save draft');
        }

        if (fnRes.organization_id) {
          dataToSave.organization_id = fnRes.organization_id;
          rememberDraftToken(fnRes.organization_id, fnRes.draft_token);
          // Keep the session stamp in step with the draft so a later EIN check
          // excludes this organisation after the connections step rewrites company data.
          markOnboardingSessionOwner(fnRes.organization_id);
          // Persist to sessionStorage for future navigation
          const currentSessionData = sessionStorage.getItem('figabl_company_data');
          if (currentSessionData) {
            try {
              const parsedSessionData = JSON.parse(currentSessionData);
              parsedSessionData.organization_id = fnRes.organization_id;
              sessionStorage.setItem('figabl_company_data', JSON.stringify(parsedSessionData));
            } catch (error) {
              console.error('Error updating sessionStorage with organization_id:', error);
            }
          }
        }

        // Generate or use existing submission identifier
        let finalSubmissionIdentifier = submissionIdentifier;

        if (submissionIdentifier === 'default') {
          // Generate a stable identifier for this email
          finalSubmissionIdentifier = generateStableIdentifier(emailToUse);
          // Store it in sessionStorage for consistency
          sessionStorage.setItem('figabl_submission_identifier', finalSubmissionIdentifier);
        }

        // Store submission info in magic_link_sessions table
        const { data: existingSession, error: sessionFetchError } = await supabase
          .from('magic_link_sessions')
          .select('*')
          .eq('email', emailToUse)
          .eq('submission_identifier', finalSubmissionIdentifier)
          .maybeSingle();

        if (sessionFetchError && sessionFetchError.code !== 'PGRST116') {
          console.error('Error checking existing magic link session:', sessionFetchError);
        }

        if (!existingSession) {
          const { data, error: sessionError } = await invokeFunction(supabase, 'update-magic-link-session', {
            body: {
              action: 'insert',
              sessionData: {
                email: emailToUse,
                submission_identifier: finalSubmissionIdentifier,
                organisation_id: dataToSave.organization_id || null
              }
            }
          });

          if (sessionError || !data?.success) {
            console.error('Error creating magic link session:', sessionError?.message || data?.error);
          }
        }

        dataToSave.terms = true
        setCompanyData(dataToSave);
        toast({
          title: "Progress saved",
          description: "Your information has been saved."
        });

        draftSaved = true;
        return true;
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to save your progress. Please try again.",
          variant: "destructive"
        });
        return false;
      } finally {
        finishConnectionSave(connectionSave, draftSaved);
      }
    }

    try {
      // Generate or use existing submission identifier for authenticated users too
      let finalSubmissionIdentifier = submissionIdentifier;

      if (submissionIdentifier === 'default') {
        finalSubmissionIdentifier = generateStableIdentifier(emailToUse);
        sessionStorage.setItem('figabl_submission_identifier', finalSubmissionIdentifier);
      }

      // Update magic_link_sessions if we have a user
      const { data: existingSession, error: sessionFetchError } = await supabase
        .from('magic_link_sessions')
        .select('*')
        .eq('email', currentSession.user.email || emailToUse)
        .eq('submission_identifier', finalSubmissionIdentifier)
        .maybeSingle();

      if (sessionFetchError && sessionFetchError.code !== 'PGRST116') {
        console.error('Error checking existing magic link session:', sessionFetchError);
      }

      if (!existingSession) {
        const { data, error: sessionError } = await invokeFunction(supabase, 'update-magic-link-session', {
          body: {
            action: 'insert',
            sessionData: {
              email: currentSession.user.email || emailToUse,
              submission_identifier: finalSubmissionIdentifier,
              organisation_id: dataToSave.organization_id || null
            }
          }
        });

        if (sessionError || !data?.success) {
          console.error('Error creating magic link session:', sessionError?.message || data?.error);
        }
      }

      // Use edge function to save draft (authenticated)
      const organizationData = {
        poc_name: dataToSave.pocName,
        poc_email: dataToSave.pocEmail,
        poc_phone: dataToSave.pocPhone || null,
        poc_phone_verified: dataToSave.pocPhoneVerified || false,
        name: dataToSave.name,
        name_dbas: dataToSave.name_dbas || null,
        address: dataToSave.address || '',
        state_of_incorporation: dataToSave.stateOfIncorporation || '',
        ein: dataToSave.ein || '',
        products: dataToSave.products as unknown as Json,
        connections: dataToSave.connections as unknown as Json,
        revenue_last_12_months: dataToSave.revenueLast12Months || null,
        onboarding_state: 'in_progress' as const,
      };

      const { data: fnRes, error: fnErr } = await invokeFunction(supabase, 'organisations-manager-v3', {
        body: {
          action: 'save_draft',
          data: organizationData,
          organization_id: dataToSave.organization_id || null,
          ...draftTokenField(dataToSave.organization_id),
        }
      });

      if (fnErr || !fnRes?.success) {
        console.error('Edge function error (save_draft auth):', fnErr || fnRes);
        throw fnErr || new Error(fnRes?.error || 'Failed to save draft');
      }

      if (fnRes.organization_id) {
        dataToSave.organization_id = fnRes.organization_id;
        markOnboardingSessionOwner(fnRes.organization_id);
        setCompanyData(dataToSave);
      }

      dataToSave.terms = true
      setCompanyData(dataToSave);
      toast({
        title: "Progress saved",
        description: "Your information has been saved. You can continue later."
      });

      draftSaved = true;
      return true;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save your progress. Please try again.",
        variant: "destructive"
      });
      return false;
    } finally {
      finishConnectionSave(connectionSave, draftSaved);
    }
  };

  const linkAnonymousDraft = async (
    userId: string,
    userEmail: string,
    submissionIdentifier: string = 'default'
  ) => {
    const connectionSnapshotEpoch = currentConnectionSaveEpoch();
    try {
      // Check if we have a specific submission for this email
      const { data: sessionData, error: sessionLookupError } = await supabase
        .from('magic_link_sessions')
        .select('*')
        .eq('email', userEmail)
        .eq('submission_identifier', submissionIdentifier)
        .maybeSingle();

      if (sessionLookupError && sessionLookupError.code !== 'PGRST116') {
        console.error('Error looking up magic link session:', sessionLookupError);
      }

      const { data: orgs, error: orgError } = await supabase
        .from('organisations')
        .select('*')
        .eq('poc_email', userEmail)
        .order('created_at', { ascending: false });

      if (orgError) {
        throw orgError;
      }

      const pinnedOrganisationId =
        typeof sessionData?.organisation_id === 'string' ? sessionData.organisation_id : null;
      let org = pickOrganisationForMagicLink(orgs ?? [], {
        sessionOrganisationId: pinnedOrganisationId,
      });

      if (!org && pinnedOrganisationId) {
        const { data: pinnedOrg, error: pinnedError } = await supabase
          .from('organisations')
          .select('*')
          .eq('id', pinnedOrganisationId)
          .maybeSingle();
        if (pinnedError) {
          console.error('Error loading magic-link organisation:', pinnedError);
        } else {
          org = pinnedOrg;
        }
      }

      if (org) {
        // `org` is already the magic-link target: the pinned session id, or the newest non-clone.
        // The email query is newest-first, so orgs[0] can be a test clone that shares this address.
        if (!org.products || !Array.isArray(org.products)) {
          org.products = parseProducts(org.products) as unknown as Json[];
        }

        // update user id
        const { error } = await supabase
          .from('organisations')
          .update({ user_id: userId })
          .eq('id', org.id);

        const formatOrg: CompanyData = {
          user_id: userId,
          pocName: org.poc_name,
          pocEmail: org.poc_email,
          pocPhone: org.poc_phone || '',
          pocPhoneVerified: org.poc_phone_verified || false,
          name: org.name,
          name_dbas: org.name_dbas,
          address: org.address,
          stateOfIncorporation: org.state_of_incorporation,
          ein: org.ein,
          revenueLast12Months: org.revenue_last_12_months || undefined,
          products: org.products as any,
          connections: seedConnectionIdsForOrg(org.id, org.connections, connectionSnapshotEpoch),
          organization_id: org.id
        };

        formatOrg.terms = true
        setCompanyData(formatOrg);

        toast({
          title: "Company loaded",
          description: "Your most recent progress has been restored."
        });
        return;
      }
    } catch (error) {
      return null;
    }
  };

  // Modified loadSavedProgress to use the submission identifier from URL or sessionStorage
  const loadSavedProgress = async (
    userId: string,
    userEmail?: string | null,
    explicitSubmissionIdentifier?: string
  ) => {
    // Use explicit submissionIdentifier if provided, otherwise get from URL/sessionStorage
    const submissionIdentifier = explicitSubmissionIdentifier || getSubmissionIdentifierFromURL();
    const connectionSnapshotEpoch = currentConnectionSaveEpoch();

    try {
      if (submissionIdentifier && submissionIdentifier !== 'default') {
        const { data: magicLinkSession, error: magicLinkSessionError } = await supabase
          .from('magic_link_sessions')
          .select('organisation_id')
          .eq('submission_identifier', submissionIdentifier)
          .maybeSingle();

        if (magicLinkSessionError && magicLinkSessionError.code !== 'PGRST116') {
          console.error('Error looking up magic link organisation:', magicLinkSessionError);
        }

        if (magicLinkSession?.organisation_id) {
          await loadOrganisationById(magicLinkSession.organisation_id, submissionIdentifier);
          return;
        }
      }

      if (userEmail) {
        // First check if the authenticated email is an alternative email
        const { data: altSessionData, error: altSessionError } = await (supabase
          .rpc as any)('find_session_by_alternative_email', { search_email: userEmail });

        if (altSessionError) {
          console.error('Error checking alternative email:', altSessionError);
        }

        // Find the matching session with the right submission identifier
        const hasAlternativeEmailMatch = Array.isArray(altSessionData) && altSessionData.length > 0;
        const alternativeEmailData = hasAlternativeEmailMatch
          ? altSessionData.find(session => session.submission_identifier === submissionIdentifier)
          : null;

        // If user logged in with alternative email, load the organization associated with that email
        if (alternativeEmailData?.organisation_id) {
          await loadOrganisationById(alternativeEmailData.organisation_id, submissionIdentifier);
          return;
        }

        // Continue with normal flow - try to link anonymous draft
        const linkedDraft = await linkAnonymousDraft(userId, userEmail, submissionIdentifier);
        if (linkedDraft) {
          linkedDraft.terms = true;
          setCompanyData(linkedDraft);
          return;
        }
      }

      // Load merchants still onboarding (FIG-345 UC-225: onboarding_state, not status)
      const { data: orgs, error: orgError } = await supabase
        .from('organisations')
        .select('*')
        .or(`user_id.eq.${userId},poc_email.eq.${userEmail}`)
        .filter('onboarding_state', 'neq', 'submitted') // see the EIN lookup above
        .eq('is_test_clone', false)
        .order('created_at', { ascending: false });

      if (orgError) {
        throw orgError;
      }

      const org = pickOrganisationForMagicLink(orgs ?? []);
      if (org) {


        // update user id if needed
        if (!org.user_id) {
          const { error } = await supabase
            .from('organisations')
            .update({ user_id: userId })
            .eq('id', org.id);
        }

        const formatOrg: CompanyData = {
          user_id: userId,
          pocName: org.poc_name,
          pocEmail: org.poc_email,
          pocPhone: org.poc_phone || '',
          pocPhoneVerified: org.poc_phone_verified || false,
          name: org.name,
          name_dbas: org.name_dbas,
          address: org.address,
          stateOfIncorporation: org.state_of_incorporation,
          ein: org.ein,
          revenueLast12Months: org.revenue_last_12_months || undefined,
          products: parseProducts(org.products),
          connections: seedConnectionIdsForOrg(org.id, org.connections, connectionSnapshotEpoch),
          organization_id: org.id
        };

        setDisableEin(true)
        formatOrg.terms = true
        setCompanyData(formatOrg);

        toast({
          title: "Draft loaded",
          description: "Your most recent progress has been restored."
        });
        return;
      }

      // Also check for submitted organizations (for users who want to edit)
      const { data: submittedOrgs, error: submittedOrgError } = await supabase
        .from('organisations')
        .select('*')
        .or(`user_id.eq.${userId},poc_email.eq.${userEmail}`)
        .filter('onboarding_state', 'eq', 'submitted') // see the EIN lookup above
        .eq('is_test_clone', false)
        .order('created_at', { ascending: false });

      if (submittedOrgError) {
        throw submittedOrgError;
      }

      const submittedOrg = pickOrganisationForMagicLink(submittedOrgs ?? []);
      if (submittedOrg) {


        // update user id if needed
        if (!submittedOrg.user_id) {
          await supabase
            .from('organisations')
            .update({ user_id: userId })
            .eq('id', submittedOrg.id);
        }

        const formatOrg: CompanyData = {
          user_id: userId,
          pocName: submittedOrg.poc_name,
          pocEmail: submittedOrg.poc_email,
          pocPhone: submittedOrg.poc_phone || '',
          pocPhoneVerified: submittedOrg.poc_phone_verified || false,
          name: submittedOrg.name,
          name_dbas: submittedOrg.name_dbas,
          address: submittedOrg.address,
          stateOfIncorporation: submittedOrg.state_of_incorporation,
          ein: submittedOrg.ein,
          revenueLast12Months: submittedOrg.revenue_last_12_months || undefined,
          products: parseProducts(submittedOrg.products),
          connections: seedConnectionIdsForOrg(submittedOrg.id, submittedOrg.connections, connectionSnapshotEpoch),
          organization_id: submittedOrg.id
        };

        // The draft lookup above found nothing, so this submitted row owns the session.
        setDisableEin(true)
        formatOrg.terms = true
        setCompanyData(formatOrg);

        toast({
          title: "Organization loaded",
          description: "Your most recent submission has been restored."
        });
        return;
      }

      try {
        const { data: submissions, error: submissionError } = await supabase
          .from('form_submissions')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });

        if (submissionError) {
          throw submissionError;
        }

        if (submissions && submissions.length > 0) {
          const mostRecentSubmission = submissions[0];
          const submissionData = mostRecentSubmission.company_data as unknown as CompanyData;

          if (!submissionData.products || !Array.isArray(submissionData.products)) {
            submissionData.products = submissionData.products || [];
          }

          // Ensure phone verification status is preserved from submission data
          if (!submissionData.pocPhone) {
            submissionData.pocPhone = '';
          }
          if (submissionData.pocPhoneVerified === undefined) {
            submissionData.pocPhoneVerified = false;
          }

          // Normalize connections from submission data
          if (submissionData.connections && !Array.isArray(submissionData.connections)) {
            const connectionsArray: Connection[] = [];
            if (submissionData.connections && typeof submissionData.connections === 'object') {
              for (const key in submissionData.connections as any) {
                const conn = (submissionData.connections as any)[key];
                if (conn) {
                  connectionsArray.push({
                    ...conn,
                    id: key
                  });
                }
              }
            }
            submissionData.connections = connectionsArray;
          } else if (!submissionData.connections) {
            submissionData.connections = [];
          }

          submissionData.terms = true
          setCompanyData(submissionData);
          toast({
            title: "Previous submission loaded",
            description: "You can review and update your information."
          });
        }
      } catch (error) {
        console.error(error)
        // Silently handle errors
      }
    } catch (error) {
      if (error instanceof Error && error.message !== 'No rows returned') {
        console.error("An unexpected error occurred while loading your progress.", error);
      }
    }
  };

  const loadDraftById = async (organizationId: string) => {
    const connectionSnapshotEpoch = currentConnectionSaveEpoch();
    try {
      setIsLoading(true);
      const { data: org, error } = await supabase
        .from('organisations')
        .select('*')
        .eq('id', organizationId)
        .single();

      if (error) {
        throw error;
      }

      if (org) {
        const formatOrg: CompanyData = {
          user_id: org.user_id,
          pocName: org.poc_name,
          pocEmail: org.poc_email,
          pocPhone: org.poc_phone || '',
          pocPhoneVerified: org.poc_phone_verified || false,
          name: org.name,
          name_dbas: org.name_dbas || '',
          address: org.address,
          stateOfIncorporation: org.state_of_incorporation,
          ein: org.ein,
          revenueLast12Months: org.revenue_last_12_months || undefined,
          products: parseProducts(org.products),
          connections: seedConnectionIdsForOrg(org.id, org.connections, connectionSnapshotEpoch),
          organization_id: organizationId
        };

        formatOrg.terms = true

        // Seed runs while building formatOrg, before this write. A clear on adoption
        // must not wipe the company data just recorded.
        setCompanyData(formatOrg);
        sessionStorage.setItem('figabl_company_data', JSON.stringify(formatOrg));

        toast({
          title: "Organization loaded",
          description: "You are now editing this organization."
        });
      }
    } catch (error) {
      console.error('Error loading organization by ID:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkInitialSession = async () => {
      try {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hasAuthInHash = hashParams.has('access_token') || hashParams.has('refresh_token');
        const token = getTokenFromURL();
        const submissionIdentifier = getSubmissionIdentifierFromURL();

        if (hasAuthInHash || token) {
          setTokenDetected(true);
          setIsVerifyingToken(true);

          if (token) {
            await verifyMagicLink(token);
          } else {
            const { data, error } = await supabase.auth.getSession();
            if (error) {
              throw error;
            }

            if (data.session) {
              setSession(data.session);
              await loadSavedProgress(data.session.user.id, data.session.user.email, submissionIdentifier);
            } else {
              throw new Error('Authentication failed. Please try logging in again.');
            }
          }

          return;
        }

        const { data: { session: initialSession } } = await supabase.auth.getSession();

        if (initialSession?.user) {
          setSession(initialSession);

          // Enhanced handling for session without token
          const userEmail = initialSession.user.email;
          const submissionIdentifier = getSubmissionIdentifierFromURL();

          // First check if the authenticated email is an alternative email
          if (userEmail) {
            const { data: altSessionData, error: altSessionError } = await (supabase
              .rpc as any)('find_session_by_alternative_email', { search_email: userEmail });

            if (altSessionError) {
              console.error('Error checking alternative email:', altSessionError);
            }

            // Find the matching session with the right submission identifier
            const hasAlternativeEmailMatch = Array.isArray(altSessionData) && altSessionData.length > 0;
            const alternativeEmailData = hasAlternativeEmailMatch
              ? altSessionData.find(session => session.submission_identifier === submissionIdentifier)
              : null;

            // If user logged in with alternative email, load the organization associated with that email
            if (alternativeEmailData?.organisation_id) {
              await loadOrganisationById(alternativeEmailData.organisation_id, submissionIdentifier);
              setIsLoading(false);
              return;
            }
          }

          // Continue with regular flow
          await loadSavedProgress(initialSession.user.id, initialSession.user.email, submissionIdentifier);
        }

        setIsLoading(false);
      } catch (error) {
        toast({
          title: "Session error",
          description: "There was an issue loading your session",
          variant: "destructive"
        });
        setIsLoading(false);
      }
    };

    if (!isVerifyingToken) {
      checkInitialSession();
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (isVerifyingToken) {
        return;
      }

      if (event === 'SIGNED_IN' && newSession?.user) {
        setSession(newSession);

        setTimeout(async () => {
          const submissionIdentifier = getSubmissionIdentifierFromURL();

          if (pendingSave) {
            await saveDraft(pendingSave, newSession, submissionIdentifier);
            setPendingSave(null);
          }

          // Check if this is an alternative email
          if (newSession.user.email) {
            const { data: altSessionData, error: altSessionError } = await (supabase
              .rpc as any)('find_session_by_alternative_email', { search_email: newSession.user.email });

            if (altSessionError) {
              console.error('Error checking alternative email:', altSessionError);
            }

            // Find the matching session with the right submission identifier
            const hasAlternativeEmailMatch = Array.isArray(altSessionData) && altSessionData.length > 0;
            const alternativeEmailData = hasAlternativeEmailMatch
              ? altSessionData.find(session => session.submission_identifier === submissionIdentifier)
              : null;

            if (alternativeEmailData?.organisation_id) {
              await loadOrganisationById(alternativeEmailData.organisation_id, submissionIdentifier);
              setIsLoading(false);
              return;
            }
          }

          await loadSavedProgress(newSession.user.id, newSession.user.email, submissionIdentifier);
          setIsLoading(false);
        }, 0);
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [isVerifyingToken, pendingSave, location.search]);

  return {
    companyData,
    setCompanyData,
    formCompleted,
    setFormCompleted,
    session,
    pendingSave,
    setPendingSave,
    isLoading,
    isVerifyingToken,
    saveDraft,
    tokenDetected,
    loadDraftById,
    loadOrganisationById,
    disableEin
  };
};
